import {
  exitTaskFocus,
  selectIsTaskFocusActive,
  selectTaskFocusConversationIds,
  setTaskFocusConversations,
} from './taskFocusDuck';

/**
 * Keeps the chat's focus list (taskFocusDuck) on the person's tasks in
 * progress (Inicio v2, spec S3). Two sources move it:
 * - Pusher `taskUpdated` ({ taskId, status, clientId }) moves one
 *   conversation at once, on any screen (realtime/human-tasks-realtime.js);
 * - every settled read of `GET /human-tasks/me` confirms the whole list
 *   (components/conversations/useTaskFocusSync.js).
 * A read that arrives within FOCUS_SYNC_GRACE_MS of a local change («Tomar»,
 * a Pusher event) may have been requested before it, so it can only add.
 * When the last task in progress ends, focus exits.
 *
 * Assumes one open task per conversation (the assigner refreshes the open
 * task of a conversation instead of creating another).
 */

export const FOCUS_SYNC_GRACE_MS = 15000;

const IN_PROGRESS_STATUSES = ['taken', 'paused'];

const isId = (value) => typeof value === 'string' && value !== '';

/** The list after one task changed: in progress adds its conversation, any other status removes it. */
export const focusIdsAfterTaskUpdate = (ids, data) => {
  if (!data || !isId(data.clientId) || !isId(data.status)) return ids;
  const { clientId } = data;
  if (IN_PROGRESS_STATUSES.includes(data.status)) {
    return ids.includes(clientId) ? ids : [...ids, clientId];
  }
  return ids.includes(clientId) ? ids.filter((id) => id !== clientId) : ids;
};

export const isWithinFocusSyncGrace = (localChangeAt, now = Date.now()) =>
  typeof localChangeAt === 'number' && now - localChangeAt < FOCUS_SYNC_GRACE_MS;

const sameIds = (a, b) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Stores `next`; when focus is on and the list went from some conversations
 * to none (the last task ended), focus exits. A focus entered with no tasks
 * stays until the person leaves it.
 */
const storeFocusIds = (next) => (dispatch, getState) => {
  const current = selectTaskFocusConversationIds(getState());
  if (!sameIds(current, next)) dispatch(setTaskFocusConversations(next));
  if (selectIsTaskFocusActive(getState()) && current.length > 0 && next.length === 0) {
    dispatch(exitTaskFocus());
  }
  return selectTaskFocusConversationIds(getState());
};

/** Pusher `taskUpdated`: one conversation in or out. */
export const applyTaskUpdateToFocusAction = (data) => (dispatch, getState) => {
  const current = selectTaskFocusConversationIds(getState());
  const next = focusIdsAfterTaskUpdate(current, data);
  if (next === current) return current;
  return dispatch(storeFocusIds(next));
};

/**
 * A settled `/human-tasks/me` read: its conversations in progress replace the
 * list, or are only added to it with `additiveOnly`. Resolves the stored list.
 */
export const syncTaskFocusFromServerAction = (serverIds, { additiveOnly = false } = {}) => (dispatch, getState) => {
  const server = (Array.isArray(serverIds) ? serverIds : []).filter(isId);
  const current = selectTaskFocusConversationIds(getState());
  const next = additiveOnly ? [...current, ...server.filter((id) => !current.includes(id))] : server;
  return dispatch(storeFocusIds(next));
};
